import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { Button, Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import {
  ContainerWithLoader,
  PaginationWithLimit,
  StatusColorLegend,
} from "../../../../components/ui";

import { APIPaymentCards } from "../../../../api";
import {
  IPaymentCard,
  IUpdatePaymentCardRequestData,
} from "../../../../api/paymentCards/index.types";

import { customerActions } from "../../../../reducers/customer";
import usePagination from "../../../../hooks/usePagination";
import { StyledTable } from "../../../../useGeneralStyles";
import { PAYMENT_CARDS_API_URLS } from "../../../../api/paymentCards";
import { STATUS_COLORS_OPACITY } from "../../../../constants";
import TableHead from "./TableHead";
import TableBody from "./TableBody";
import SharedIPsDialog from "./SharedICardsDialog";

const PaymentCards = (): React.ReactElement => {
  const params: { id: string } = useParams();
  const accounts_id = +params.id;
  const dispatch = useDispatch();
  const pagination = usePagination(5);

  const {
    loading,
    data: { payment_cards },
  } = useSelector((state) => state.customer.payment_cards);

  const fetchCustomerPaymentCards = useQuery({
    retry: false,
    queryKey: [
      PAYMENT_CARDS_API_URLS.getCustomerPaymentCards,
      accounts_id,
      pagination.limit,
      pagination.page,
    ],
    async queryFn() {
      dispatch(customerActions.fetchPaymentCardsRequest());
      const customer_cards = await APIPaymentCards.getCustomerPaymentCardsRequest(accounts_id, {
        limit: pagination.limit,
        page: pagination.page,
      });
      dispatch(customerActions.fetchPaymentCardsSuccess(customer_cards.data));
      return customer_cards;
    },
  });
  useEffect(() => {
    if (fetchCustomerPaymentCards.isError && fetchCustomerPaymentCards.error) {
      dispatch(customerActions.fetchPaymentCardsFailed());
      console.error("Error get customer payment cards", fetchCustomerPaymentCards.error);
    }
  }, [fetchCustomerPaymentCards.isError, fetchCustomerPaymentCards.error]);

  const handleSubmitForm = async (formValues: { payment_cards: IPaymentCard[] }) => {
    try {
      const updated_payment_cards: IUpdatePaymentCardRequestData[] = [];
      formValues.payment_cards.forEach((card, index) => {
        const { active, def } = payment_cards.cards[index];
        if (active !== card.active || def !== card.def) {
          updated_payment_cards.push({
            id: card.id,
            active: card.active,
            def: card.def,
          });
        }
        return;
      });

      await APIPaymentCards.updatePaymentCardRequest(accounts_id, updated_payment_cards);
      await fetchCustomerPaymentCards.refetch();
    } catch (error) {
      console.error("Error update payment cards", error);
    }
  };

  const [selectedCardId, setSelectedCardId] = React.useState<number | null>(null);

  return (
    <Box mb="20px">
      <SharedIPsDialog
        card={selectedCardId}
        handleClose={() => {
          setSelectedCardId(null);
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="baseline">
        <h4>Payment cards</h4>

        <Box sx={{ justifyContent: "flex-end", display: "flex", marginBottom: "10px" }}>
          <StatusColorLegend
            legend={{
              active: {
                color: STATUS_COLORS_OPACITY.lightGreen,
                name: "Active",
              },
              shared: {
                color: STATUS_COLORS_OPACITY.tango,
                name: "Shared card",
              },
            }}
          />
        </Box>
      </Box>
      <ContainerWithLoader isLoading={loading} report={payment_cards.cards}>
        <Formik
          initialValues={{ payment_cards: payment_cards.cards }}
          enableReinitialize
          onSubmit={handleSubmitForm}
        >
          {({ values, resetForm, dirty }) => {
            return (
              <Form>
                <StyledTable tableWithoutHover>
                  <TableHead />
                  <TableBody
                    payment_cards={values.payment_cards}
                    setSelectedCardId={setSelectedCardId}
                  />
                </StyledTable>

                {dirty && (
                  <Box>
                    <Button
                      sx={{ marginRight: "18px" }}
                      color="secondary"
                      size="small"
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button size="small" onClick={() => resetForm()}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Form>
            );
          }}
        </Formik>
      </ContainerWithLoader>

      <PaginationWithLimit
        count={payment_cards.count}
        limit={pagination.limit}
        page={pagination.page}
        setPageState={pagination.setPage}
        setLimit={pagination.setLimit}
      />
    </Box>
  );
};

export default PaymentCards;
